import Container from '@/components/@base/container';
import Timer from '@/components/timer';
import ozone_text_logo_light from '@/assets/logos/ozone-text-logo-light.svg';
import ozone_text_logo_dark from '@/assets/logos/ozone-text-logo-dark.svg';
import useCommonModalStore from '@/store/common-modal-store';
import CancelPayment from '@/components/cancel-payment';
import { useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import FailedPayment from '@/components/failed-payment';
import FontIcon from '@/components/@base/font-icon';
import useBottomSheetStore from '@/store/bottom-sheet-store';
import ActiveEditWallets from '@/components/active-edit-wallets-sheet';
import { Web } from 'sip.js';
import * as signalR from '@microsoft/signalr';
import { API_ROUTES, SOCKET_API } from '@/constant/routes';
import { useGetAdvertisement, useGetInvoices, usePostRequestCall } from '@/services/hooks';
import cn from '@/lib/clsxm';
import Support from '@/components/support';
import { AnimatePresence } from 'framer-motion';
import { changeFavicon, isDesktop } from '@/lib/helper';
import { Text } from '@/components/@base/typography';
import { SIZE_ENUM } from '@/components/@base/@types';
import Button from '@/components/@base/button';
import useUserWalletsVouchersStore from '@/store/user-wallets-store';
import localization from '@/constant/localization';
import Spinner from '@/components/Spinner';

const Header = ({ showAds = false }: { showAds?: boolean }) => {
  const [showSupport, setShowSupport] = useState(false);
  const [userAgent, setUserAgent] = useState<Web.SimpleUser | null>(null);
  const [status, setStatus] = useState<string>('در حال برقراری ارتباط ...');
  const [callLoading, setCallLoading] = useState<boolean>(false);
  const [supportData, setSupportData] = useState<any>(null);

  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const { segments } = useUserWalletsVouchersStore();
  const { setShowBottomSheet } = useBottomSheetStore();
  const { data, isPending } = useGetInvoices();
  const { setShow } = useCommonModalStore();
  const [searchParams] = useSearchParams();
  const { data: advertise } = useGetAdvertisement(7, searchParams.get('pgc') as string);
  const { mutate: requestCall } = usePostRequestCall();
  useEffect(() => {
    const mUpgData = sessionStorage.getItem('mUpg')
      ? JSON.parse(sessionStorage.getItem('mUpg')!)
      : null;
    if(mUpgData){
      document.title = `${mUpgData.title}`
    } else{
      document.title = localization.ozoneGateWay
      changeFavicon('/favicon.ico');
    }
  }, []);
  const startSocket = useCallback(async () => {
    if (connectionRef.current) return;

    const token = localStorage.getItem('access_token');
    if (!token) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${SOCKET_API}/socket/upgHub`, {
        accessTokenFactory: () => token,
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .build();

    try {
      await connection.start();
      connectionRef.current = connection;
    } catch (err) {
      // console.error('[USER] Connection failed:', err);
      try {
        await connection.stop();
      } catch {}
    }
  }, []);

  const handleCancelPayment = () => {
    setShow(true, {
      center: true,
      dialogPanelClassName: 'px-0 pb-0 w-[85%] md:w-[380px]',
      Body: () => <CancelPayment />,
    });
  };

  const handleActivateEditeWallets = () => {
    if (segments.length === 0) return;
    isDesktop()
      ? setShow(true, {
          Body: () => <ActiveEditWallets />,
        })
      : setShowBottomSheet(true, {
          bgLighter: true,
          Body: () => <ActiveEditWallets />,
        });
  };

  const callToSupport = async () => {
    setCallLoading(true);
    setShow(false);

    let micStream: MediaStream | null = null;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      // console.error('Mic permission denied:', err);
      setStatus('دسترسی به میکروفون رد شد');
      setCallLoading(false);
      return;
    }

    requestCall(undefined, {
      onSuccess: async (res) => {
        setSupportData(res.data);
        setShowSupport(true);

        const { socketUrl, clientExtension, serverExtension, domain, password } = res.data;

        const options: Web.SimpleUserOptions = {
          aor: `sip:${clientExtension}@${domain}`,
          media: {
            remote: {
              audio: remoteAudioRef.current!,
            },
          },
          userAgentOptions: {
            displayName: 'Web Client',
            authorizationUsername: clientExtension,
            authorizationPassword: password,
            sessionDescriptionHandlerFactoryOptions: {
              peerConnectionConfiguration: {
                iceServers: [
                  { urls: 'stun:stun.l.google.com:19302' },
                  { urls: 'stun:stun1.l.google.com:19302' },
                ],
              },
            },
          },
        };

        const simpleUser = new Web.SimpleUser(socketUrl, options);

        simpleUser.delegate = {
          onCallAnswered: () => {
            setStatus('در تماس');
            if (remoteAudioRef.current) {
              remoteAudioRef.current.play().catch(() => {});
            }
          },
          onCallHangup: () => {
            setStatus('پایان تماس');
            setTimeout(() => callSupport('close'), 2000);
          },
          onRegistered: () => setStatus('آنلاین'),
          onUnregistered: () => setStatus('آفلاین'),
          onServerConnect: () => setStatus('متصل'),
          onServerDisconnect: () => setStatus('قطع ارتباط'),
        };

        try {
          await simpleUser.connect();
          await simpleUser.register();

          const targetURI = `sip:${serverExtension}@${domain}`;
          await simpleUser.call(targetURI);

          setUserAgent(simpleUser);
          setShowSupport(true);
        } catch (err) {
          // console.error('Connection failed:', err);
          setStatus('خطا در ارتباط');
          micStream?.getTracks().forEach((track) => track.stop());
        }
      },
      onError: () => {
        setStatus('خطا در ارتباط');
        setCallLoading(false);
        micStream?.getTracks().forEach((track) => track.stop());
      },
    });
  };

  const callSupport = async (state: 'open' | 'close') => {
    if (state === 'open') {
      setShow(true, {
        center: true,
        Body: () => (
          <Container center className='flex-col p-4 pt-2'>
            <FontIcon
              icon='icon-headphone'
              className='mb-6 flex h-[56px] w-[56px] items-center justify-center rounded-full bg-blue-50 text-3xl text-blue'
            />
            <Text semiBold className='mb-2 text-xl text-grayLight-1000 dark:text-grayLight-0'>
              تماس اینترنتی با پشتیبانی
            </Text>
            <Text
              medium
              size={SIZE_ENUM.SM}
              className='mb-8 text-center text-grayLight-850 dark:text-[#CCCCD1]'
            >
              برای دریافت راهنمایی، می‌توانید بدون هزینه با پشتیبانی اوزون تماس بگیرید.
            </Text>
            <Button
              className='text-md w-full whitespace-nowrap rounded-full bg-blue px-8 text-white hover:bg-blue dark:bg-blue dark:text-white dark:hover:bg-blue'
              style={{ height: '52px' }}
              size={SIZE_ENUM.XL}
              onClick={callToSupport}
              id='upg-choose-asset-selection'
            >
              تماس اینترنتی با پشتیبانی
            </Button>
            <Button
              className='text-md cursor-pointe mt-3 w-full whitespace-nowrap rounded-full bg-grayLight-100 px-8 text-grayLight-1000 hover:bg-grayLight-1000/10 dark:bg-grayDark-750 dark:text-white'
              style={{ height: '52px' }}
              size={SIZE_ENUM.XL}
              id='upg-continue-perchese-whithout-asset-selection'
              onClick={() => setShow(false)}
            >
              فعلا نه
            </Button>
          </Container>
        ),
      });
    } else if (state === 'close') {
      setCallLoading(false);
      setShowSupport(false);
      if (userAgent) {
        userAgent.hangup().catch(console.error);
        userAgent.unregister().catch(console.error);
        userAgent.disconnect().catch(console.error);
        setUserAgent(null);
      }
      navigator.mediaDevices.getUserMedia({ audio: false }).catch(() => {});
      setCallLoading(false);
      setShowSupport(false);
      setStatus('قطع تماس');
    }
  };

  useEffect(() => {
    startSocket();
  }, [startSocket]);

  useEffect(() => {
    if (searchParams.has('IPGStatus') && searchParams.get('IPGStatus') === 'Failed') {
      setShow(true, {
        center: true,
        dialogPanelClassName: 'px-0 pb-0 w-[280px]',
        Body: () => <FailedPayment />,
      });
    }
  }, [searchParams, setShow]);

  useEffect(() => {
    return () => {
      if (userAgent) {
        userAgent.unregister().catch(console.error);
        userAgent.disconnect().catch(console.error);
      }

      const connection = connectionRef.current;
      connectionRef.current = null;

      if (connection) {
        connection.stop().catch((err) => {
          // console.error('[USER] stop connection error:', err);
        });
      }
    };
  }, [userAgent]);

  return (
    <Container
      center
      className={cn(
        'w-full flex-col gap-3 border-b border-grayLight-50 px-3 pb-3 pt-4 dark:border-grayDark-750',
        advertise && advertise?.ads?.length > 0 && 'border-none pb-1',
      )}
    >
      <AnimatePresence mode='wait'>
        {showSupport && (
          <Support
            supportData={supportData}
            status={status}
            cancelCall={() => callSupport('close')}
          />
        )}
      </AnimatePresence>

      <Container center className='relative w-full justify-between'>
        <Container center className='gap-2'>
          {!isPending && !data?.isEpg ? (
            <FontIcon
              icon='icon-slider-horizontal'
              onClick={handleActivateEditeWallets}
              className={cn(
                'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-grayLight-50 text-lg dark:bg-grayDark-750 dark:text-white',
                segments.length === 0 &&
                  'cursor-not-allowed text-grayLight-350 dark:text-grayLight-650',
              )}
            />
          ) : (
            <Container center className='gap-3'>
              <img
                src={data?.epgLogo}
                alt={data?.epgTitle}
                className='h-10 w-10 rounded-full'
              />
              <Text className='text-black dark:text-white'>{data?.epgTitle}</Text>
            </Container>
          )}

          {localStorage.getItem("call") &&
            (callLoading ? (
              <Container className='flex h-10 w-10 items-center justify-center rounded-full bg-grayLight-50 text-lg dark:bg-grayDark-750 dark:text-white'>
                {showSupport ? <Container className='loading-dot' /> : <Spinner />}
              </Container>
            ) : (
              <FontIcon
                icon='icon-call-righ'
                onClick={() => callSupport('open')}
                className='flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-grayLight-50 text-lg dark:bg-grayDark-750 dark:text-white'
              />
            ))}
        </Container>

        {!isPending && !data?.isEpg && (
          <>
            <img
              src={ozone_text_logo_light}
              alt='ozone card logo'
              className={cn('absolute right-1/2 translate-x-1/2 dark:hidden')}
            />
            <img
              src={ozone_text_logo_dark}
              alt='ozone card logo'
              className={cn('absolute right-1/2 hidden translate-x-1/2 dark:block')}
            />
          </>
        )}
        <Container center className='gap-3'>
          {!isPending && data?.isEpg && (
            <FontIcon
              icon='icon-slider-horizontal'
              onClick={handleActivateEditeWallets}
              className={cn(
                'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-grayLight-50 text-lg dark:bg-grayDark-750 dark:text-white',
                segments.length === 0 &&
                  'cursor-not-allowed text-grayLight-350 dark:text-grayLight-650',
              )}
            />
          )}
          <Container
            center
            className='h-10 rounded-full bg-grayLight-50 p-[3px] dark:bg-grayDark-750'
          >
            <Timer />
            <Container
              center
              className='h-12 w-[40px] cursor-pointer '
              onClick={handleCancelPayment}
            >
              <FontIcon icon='icon-cross' className='text-2xl dark:text-white' />
            </Container>
          </Container>
        </Container>
      </Container>

      {advertise &&
        showAds &&
        advertise?.ads?.length > 0 &&
        advertise?.ads[0]?.banner &&
        advertise?.ads[0]?.banner?.length > 0 && (
          <Container className='relative'>
            <img
              src={advertise?.ads[0]?.banner}
              alt='ads'
              className={cn('min-h-10 w-full rounded-lg')}
            />
          </Container>
        )}

      <audio ref={remoteAudioRef} autoPlay className='hidden' />
    </Container>
  );
};

export default Header;
